import { useRef, useEffect } from 'react'

import { Box, FloraSelect, FloraSelectItem, Flex, Grid, FloraInput, FloraTypography } from '@grupoboticario/flora-react'

import { EVENT_TYPES } from '@/constants/benefitIds'
import { DEFAULT_CPF_LIMITS } from '@/constants/eventFormDefaults'
import { getEventTypeLabel } from '@/utils/eventFormatters/index'
import { sanitizeHtmlToSafeHtml } from '@/utils/htmlHelpers/htmlHelpers'

import DescriptionEditor from './DescriptionEditor'
import ImageUpload from './ImageUpload'
import {
  mainBoxStyle,
  mainTextStyle,
  mainGridStyle,
  pointsBoxStyle,
  descriptionRowCss,
  limitGridStyle
} from './MainInformation.styles'
import { MainInformationProps } from './MainInformation.types'

const EVENT_TYPE_OPTIONS = [EVENT_TYPES.EXCLUSIVE_EVENTS, EVENT_TYPES.FACTORY_VISIT]

const MainInformation = ({
  values,
  setFieldValue,
  imagePreview,
  setImagePreview,
  getSignedUrl,
  isReadOnly
}: MainInformationProps) => {
  const descricaoRef = useRef<HTMLDivElement>(null)
  const nomeRef = useRef<HTMLTextAreaElement & HTMLInputElement>(null)

  useEffect(() => {
    if (descricaoRef.current && values.description) {
      descricaoRef.current.innerHTML = sanitizeHtmlToSafeHtml(values.description)
    }
  }, [values.description])

  useEffect(() => {
    if (nomeRef.current && values.name !== undefined) {
      nomeRef.current.value = values.name
    }
  }, [values.name])

  const handleDescricaoBlur = () => {
    const content = sanitizeHtmlToSafeHtml(descricaoRef.current?.innerHTML ?? '')
    setFieldValue('description', content)
  }

  return (
    <Box css={mainBoxStyle}>
      <FloraTypography css={mainTextStyle} fontSize="bodyLargeStandard" fontWeight="medium">
        Informações principais
      </FloraTypography>

      <Grid css={mainGridStyle}>
        <Box>
          <FloraSelect
            id="tipo"
            label="Tipo:"
            onSelected={(selected) => setFieldValue('type', selected?.value ?? '')}
            placeholder="Selecionar"
            required
            value={values.type}
            size="medium"
          >
            {EVENT_TYPE_OPTIONS.map((type) => (
              <FloraSelectItem key={type} label={getEventTypeLabel(type)} value={type} />
            ))}
          </FloraSelect>
        </Box>

        <Box>
          <FloraInput
            as="textarea"
            defaultValue={values.name}
            id="nome"
            label="Nome:"
            onChange={(e) => setFieldValue('name', e.target.value)}
            ref={nomeRef}
            required
            size="medium"
          />
        </Box>
      </Grid>

      <Flex direction="row" gap={'$4'} css={descriptionRowCss}>
        <DescriptionEditor
          descricaoRef={descricaoRef}
          description={values.description}
          isReadOnly={isReadOnly}
          onBlur={handleDescricaoBlur}
        />

        {values.type === 'EXCLUSIVE_EVENTS' && (
          <ImageUpload
            getSignedUrl={getSignedUrl}
            imagePreview={imagePreview}
            onImageChange={(imageKey, previewUrl) => {
              setFieldValue('image', imageKey)
              setFieldValue('imagePreviewUrl', previewUrl)
            }}
            setImagePreview={setImagePreview}
          />
        )}
      </Flex>

      <Box css={pointsBoxStyle}>
        <FloraInput
          id="pontosIngresso"
          label="Pontos por ingresso:"
          mask="xxxxx"
          onChange={(e) => setFieldValue('pointsPerTicket', e.target.value)}
          required
          size="medium"
          value={values.pointsPerTicket || ''}
        />
      </Box>

      <Grid css={limitGridStyle}>
        {values.type !== EVENT_TYPES.FACTORY_VISIT && (
          <FloraSelect
            id="eventLimitPerCpf"
            label="Limite por CPF (evento):"
            onSelected={(selected) => setFieldValue('eventLimitPerCpf', selected?.value ?? '')}
            placeholder="Deixe vazio para sem limite geral"
            value={values.eventLimitPerCpf || ''}
            size="medium"
          >
            {DEFAULT_CPF_LIMITS.map((limit) => (
              <FloraSelectItem key={limit} label={String(limit)} value={String(limit)} />
            ))}
          </FloraSelect>
        )}
      </Grid>
    </Box>
  )
}

export default MainInformation
